import { SixButton, SixDatepicker, SixInput, SixMenuItem, SixSelect, SixTab, SixTabGroup, SixTabPanel } from "@six-group/ui-library-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { capitalizeWord } from "../Utils/contentTransformers";
import { backEndEnvironmentUrlToUse } from "../Utils/environmentHelpers";
import { LATEST_MAJOR_VERSION } from "../Assets/variables";

const InputTypes = {
    DateRange: "dateRange",
    Select: "select",
    NumberRange: "numberRange"
}

const criteriaConfig = {
    "Contract Date Day From": { type: InputTypes.NumberRange },
    "Contract Date Day To": { type: InputTypes.NumberRange },
    "Contract Date Month From": { type: InputTypes.NumberRange },
    "Contract Date Month To": { type: InputTypes.NumberRange },
    "Contract Date Year From": { type: InputTypes.NumberRange },
    "Contract Date Year To": { type: InputTypes.NumberRange },
    "Contract Frequency": { type: InputTypes.Select },
    "Contract Size From": { type: InputTypes.NumberRange },
    "Contract Size To": { type: InputTypes.NumberRange },
    "Contract Type": { type: InputTypes.Select },
    "Coupon Type": { type: InputTypes.Select },
    "Current Coupon Rate From": { type: InputTypes.NumberRange },
    "Current Coupon Rate To": { type: InputTypes.NumberRange },
    "Equity Type": { type: InputTypes.Select },
    "ETD Contract Specification Type": { type: InputTypes.Select },
    "Exercise Type": { type: InputTypes.Select },
    "Expiration Date From": { type: InputTypes.DateRange },
    "Expiration Date To": { type: InputTypes.DateRange },
    "Extensions": { type: InputTypes.Select },
    "First Trading Date From": { type: InputTypes.DateRange },
    "First Trading Date To": { type: InputTypes.DateRange },
    "Instrument Status": { type: InputTypes.Select },
    "Instrument Type": { type: InputTypes.Select },
    "Issue Date From": { type: InputTypes.DateRange },
    "Issue Date To": { type: InputTypes.DateRange },
    "Issuer Scheme": { type: InputTypes.Select },
    "Listing Category": { type: InputTypes.Select },
    "Listing Status": { type: InputTypes.Select },
    "Market Scheme": { type: InputTypes.Select },
    "Maturity Date From": { type: InputTypes.DateRange },
    "Maturity Date To": { type: InputTypes.DateRange },
    "Opening Date From": { type: InputTypes.DateRange },
    "Opening Date To": { type: InputTypes.DateRange },
    "Option Right Type": { type: InputTypes.Select },
    "Option Strike Price From": { type: InputTypes.NumberRange },
    "Option Strike Price To": { type: InputTypes.NumberRange },
    "Preferred Language": { type: InputTypes.Select },
    "Right Type": { type: InputTypes.Select },
    "Security Type": { type: InputTypes.Select },
    "SIX Sector": { type: InputTypes.Select },
    "SIX Version Number": { type: InputTypes.NumberRange },
    "Settlement Type": { type: InputTypes.Select },
    "Trading Status": { type: InputTypes.Select },
    "Size": { type: InputTypes.NumberRange },
    "Underlying Scheme": { type: InputTypes.Select },
    "Underlying Instrument Type": { type: InputTypes.Select }
}

const CriteriaMetaKinds = {
    Single: "single",
    DateRangePair: "dateRangePair",
    MarketPair: "marketPair",
    IssuerPair: "issuerPair",
    UnderlyingPair: "underlyingPair",
    NumberPair: "numberPair",
};

const endsWithFrom = (name) => name.endsWith(" From");
const endsWithTo = (name) => name.endsWith(" To");
const baseNameFromRangeSuffix = (name) => name.replace(/\s+(From|To)$/, "");
const pairedNumberRangeBases = new Set(["Contract Date Day", "Contract Date Month", "Contract Date Year", "Contract Size", "Current Coupon Rate", "Option Strike Price"]);

function formatDateYYYYMMDD(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function coerceDateToYYYYMMDD(rawValue) {
    if (rawValue instanceof Date && !Number.isNaN(rawValue.getTime())) {
        return formatDateYYYYMMDD(rawValue);
    }

    if (typeof rawValue === "string") {
        const parsed = new Date(rawValue);
        if (!Number.isNaN(parsed.getTime())) {
            return formatDateYYYYMMDD(parsed);
        }
    }

    return "";
}

function buildCriteriaMetaByLabel(propertiesByEndpoint) {
    const entries = Object.entries(propertiesByEndpoint ?? {});

    const metaByLabel = {};
    const rangeGroups = new Map();
    let marketIdsKey;
    let marketSchemeKey;
    let marketIdsLabel;
    let marketSchemeLabel;
    let issuerIdsKey;
    let issuerSchemeKey;
    let issuerIdsLabel;
    let issuerSchemeLabel;
    let underlyingIdsKey;
    let underlyingSchemeKey;
    let underlyingIdsLabel;
    let underlyingSchemeLabel;

    for (const [propertyKey, property] of entries) {
        const name = property?.name ?? "";

        if (!name) {
            continue;
        }

        if (name === "Market IDs") {
            marketIdsKey = propertyKey;
            marketIdsLabel = name;
            continue;
        }

        if (name === "Market Scheme") {
            marketSchemeKey = propertyKey;
            marketSchemeLabel = name;
            continue;
        }

        if (name === "Underlying IDs") {
            underlyingIdsKey = propertyKey;
            underlyingIdsLabel = name;
            continue;
        }

        if (name === "Underlying Scheme") {
            underlyingSchemeKey = propertyKey;
            underlyingSchemeLabel = name;
            continue;
        }

        if (name === "Issuer IDs") {
            issuerIdsKey = propertyKey;
            issuerIdsLabel = name;
            continue;
        }

        if (name === "Issuer Scheme") {
            issuerSchemeKey = propertyKey;
            issuerSchemeLabel = name;
            continue;
        }

        if (endsWithFrom(name) || endsWithTo(name)) {
            const baseLabel = baseNameFromRangeSuffix(name);
            const group = rangeGroups.get(baseLabel) ?? { baseLabel };
            const criteriaType = criteriaConfig[name]?.type;

            if (!(
                criteriaType === InputTypes.DateRange
                || (criteriaType === InputTypes.NumberRange && pairedNumberRangeBases.has(baseLabel))
            )) {
                metaByLabel[name] = {
                    kind: CriteriaMetaKinds.Single,
                    label: name,
                    propertyKey,
                };
                continue;
            }

            group.kind = criteriaType === InputTypes.NumberRange ? CriteriaMetaKinds.NumberPair : CriteriaMetaKinds.DateRangePair;

            if (endsWithFrom(name)) {
                group.fromKey = propertyKey;
                group.fromLabel = name;
            }

            if (endsWithTo(name)) {
                group.toKey = propertyKey;
                group.toLabel = name;
            }

            rangeGroups.set(baseLabel, group);
            continue;
        }

        metaByLabel[name] = {
            kind: CriteriaMetaKinds.Single,
            label: name,
            propertyKey,
        };
    }

    if (underlyingIdsKey && underlyingSchemeKey) {
        metaByLabel["Underlying"] = {
            kind: CriteriaMetaKinds.UnderlyingPair,
            label: "Underlying",
            underlyingIdsKey,
            underlyingSchemeKey,
            underlyingIdsLabel,
            underlyingSchemeLabel,
        };
    } else {
        if (underlyingIdsKey && underlyingIdsLabel) {
            metaByLabel[underlyingIdsLabel] = {
                kind: CriteriaMetaKinds.Single,
                label: underlyingIdsLabel,
                propertyKey: underlyingIdsKey,
            };
        }

        if (underlyingSchemeKey && underlyingSchemeLabel) {
            metaByLabel[underlyingSchemeLabel] = {
                kind: CriteriaMetaKinds.Single,
                label: underlyingSchemeLabel,
                propertyKey: underlyingSchemeKey,
            };
        }
    }

    if (issuerIdsKey && issuerSchemeKey) {
        metaByLabel["Issuer"] = {
            kind: CriteriaMetaKinds.IssuerPair,
            label: "Issuer",
            issuerIdsKey,
            issuerSchemeKey,
            issuerIdsLabel,
            issuerSchemeLabel,
        };
    } else {
        if (issuerIdsKey && issuerIdsLabel) {
            metaByLabel[issuerIdsLabel] = {
                kind: CriteriaMetaKinds.Single,
                label: issuerIdsLabel,
                propertyKey: issuerIdsKey,
            };
        }

        if (issuerSchemeKey && issuerSchemeLabel) {
            metaByLabel[issuerSchemeLabel] = {
                kind: CriteriaMetaKinds.Single,
                label: issuerSchemeLabel,
                propertyKey: issuerSchemeKey,
            };
        }
    }

    if (marketIdsKey && marketSchemeKey) {
        metaByLabel["Market"] = {
            kind: CriteriaMetaKinds.MarketPair,
            label: "Market",
            marketIdsKey,
            marketSchemeKey,
            marketIdsLabel,
            marketSchemeLabel,
        };
    } else {
        if (marketIdsKey && marketIdsLabel) {
            metaByLabel[marketIdsLabel] = {
                kind: CriteriaMetaKinds.Single,
                label: marketIdsLabel,
                propertyKey: marketIdsKey,
            };
        }

        if (marketSchemeKey && marketSchemeLabel) {
            metaByLabel[marketSchemeLabel] = {
                kind: CriteriaMetaKinds.Single,
                label: marketSchemeLabel,
                propertyKey: marketSchemeKey,
            };
        }
    }

    for (const group of rangeGroups.values()) {
        if (group.fromKey && group.toKey) {
            metaByLabel[group.baseLabel] = {
                kind: group.kind ?? CriteriaMetaKinds.DateRangePair,
                label: group.baseLabel,
                fromKey: group.fromKey,
                toKey: group.toKey,
                fromLabel: group.fromLabel,
                toLabel: group.toLabel,
            };
        } else {
            if (group.fromKey && group.fromLabel) {
                metaByLabel[group.fromLabel] = {
                    kind: CriteriaMetaKinds.Single,
                    label: group.fromLabel,
                    propertyKey: group.fromKey,
                };
            }

            if (group.toKey && group.toLabel) {
                metaByLabel[group.toLabel] = {
                    kind: CriteriaMetaKinds.Single,
                    label: group.toLabel,
                    propertyKey: group.toKey,
                };
            }
        }
    }

    return metaByLabel;
}

function buildSelectableCriteriaOptions(availableProperties) {
    const entries = Object.entries(availableProperties ?? {});

    const options = [];
    const rangeGroups = new Map();
    let marketIdsKey;
    let marketSchemeKey;
    let underlyingIdsKey;
    let underlyingSchemeKey;
    let issuerIdsKey;
    let issuerSchemeKey;

    for (const [propertyKey, property] of entries) {
        const name = property?.name ?? "";

        if (!name) {
            continue;
        }

        if (name === "Market IDs") {
            marketIdsKey = propertyKey;
            continue;
        }

        if (name === "Market Scheme") {
            marketSchemeKey = propertyKey;
            continue;
        }

        if (name === "Issuer IDs") {
            issuerIdsKey = propertyKey;
            continue;
        }

        if (name === "Issuer Scheme") {
            issuerSchemeKey = propertyKey;
            continue;
        }

        if (name === "Underlying IDs") {
            underlyingIdsKey = propertyKey;
            continue;
        }

        if (name === "Underlying Scheme") {
            underlyingSchemeKey = propertyKey;
            continue;
        }

        if (endsWithFrom(name) || endsWithTo(name)) {
            const baseLabel = baseNameFromRangeSuffix(name);
            const criteriaType = criteriaConfig[name]?.type;

            if (!(
                criteriaType === InputTypes.DateRange
                || (criteriaType === InputTypes.NumberRange && pairedNumberRangeBases.has(baseLabel))
            )) {
                options.push({
                    id: name,
                    label: name,
                    kind: CriteriaMetaKinds.Single,
                    propertyKey,
                });
                continue;
            }

            const group = rangeGroups.get(baseLabel) ?? { baseLabel };
            group.kind = criteriaType === InputTypes.NumberRange ? CriteriaMetaKinds.NumberPair : CriteriaMetaKinds.DateRangePair;

            if (endsWithFrom(name)) {
                group.fromKey = propertyKey;
            }

            if (endsWithTo(name)) {
                group.toKey = propertyKey;
            }

            rangeGroups.set(baseLabel, group);
            continue;
        }

        options.push({
            id: name,
            label: name,
            kind: CriteriaMetaKinds.Single,
            propertyKey,
        });
    }

    if (underlyingIdsKey && underlyingSchemeKey) {
        options.push({
            id: "Underlying",
            label: "Underlying",
            kind: CriteriaMetaKinds.UnderlyingPair,
            underlyingIdsKey,
            underlyingSchemeKey,
        });
    } else {
        if (underlyingIdsKey) {
            options.push({ id: "Underlying IDs", label: "Underlying IDs", kind: CriteriaMetaKinds.Single, propertyKey: underlyingIdsKey });
        }

        if (underlyingSchemeKey) {
            options.push({ id: "Underlying Scheme", label: "Underlying Scheme", kind: CriteriaMetaKinds.Single, propertyKey: underlyingSchemeKey });
        }
    }

    if (marketIdsKey && marketSchemeKey) {
        options.push({
            id: "Market",
            label: "Market",
            kind: CriteriaMetaKinds.MarketPair,
            marketIdsKey,
            marketSchemeKey,
        });
    } else {
        if (marketIdsKey) {
            options.push({ id: "Market IDs", label: "Market IDs", kind: CriteriaMetaKinds.Single, propertyKey: marketIdsKey });
        }

        if (marketSchemeKey) {
            options.push({ id: "Market Scheme", label: "Market Scheme", kind: CriteriaMetaKinds.Single, propertyKey: marketSchemeKey });
        }
    }

    if (issuerIdsKey && issuerSchemeKey) {
        options.push({
            id: "Issuer",
            label: "Issuer",
            kind: CriteriaMetaKinds.IssuerPair,
            issuerIdsKey,
            issuerSchemeKey,
        });
    } else {
        if (issuerIdsKey) {
            options.push({ id: "Issuer IDs", label: "Issuer IDs", kind: CriteriaMetaKinds.Single, propertyKey: issuerIdsKey });
        }

        if (issuerSchemeKey) {
            options.push({ id: "Issuer Scheme", label: "Issuer Scheme", kind: CriteriaMetaKinds.Single, propertyKey: issuerSchemeKey });
        }
    }

    for (const group of rangeGroups.values()) {
        if (group.fromKey && group.toKey) {
            options.push({
                id: group.baseLabel,
                label: group.baseLabel,
                kind: group.kind ?? CriteriaMetaKinds.DateRangePair,
                fromKey: group.fromKey,
                toKey: group.toKey,
            });
        } else {
            if (group.fromKey) {
                const fromLabel = `${group.baseLabel} From`;
                options.push({ id: fromLabel, label: fromLabel, kind: CriteriaMetaKinds.Single, propertyKey: group.fromKey });
            }

            if (group.toKey) {
                const toLabel = `${group.baseLabel} To`;
                options.push({ id: toLabel, label: toLabel, kind: CriteriaMetaKinds.Single, propertyKey: group.toKey });
            }
        }
    }

    return options;
}

function InputField(props) {
    const criteria = props.criteria;
    const criteriaMeta = props.criteriaMeta;
    const propertiesByEndpoint = props.propertiesByEndpoint;
    const onRemove = props.onRemove;
    const onAdd = props.onAdd;

    const inputElementRef = useRef(null);
    const fromElementRef = useRef(null);
    const toElementRef = useRef(null);
    const marketIdsElementRef = useRef(null);
    const marketSchemeElementRef = useRef(null);
    const underlyingIdsElementRef = useRef(null);
    const underlyingSchemeElementRef = useRef(null);
    const issuerIdsElementRef = useRef(null);
    const issuerSchemeElementRef = useRef(null);

    const handleAdd = () => {
        if (criteriaMeta?.kind === CriteriaMetaKinds.DateRangePair) {
            const fromValue = coerceDateToYYYYMMDD(fromElementRef.current?.value);
            const toValue = coerceDateToYYYYMMDD(toElementRef.current?.value);
            const combined = fromValue && toValue ? `${fromValue} - ${toValue}` : (fromValue || toValue);

            if (!combined) {
                return;
            }

            onAdd?.(criteria, combined);

            return;
        }

        if (criteriaMeta?.kind === CriteriaMetaKinds.NumberPair) {
            const fromValue = String(fromElementRef.current?.value ?? "").trim();
            const toValue = String(toElementRef.current?.value ?? "").trim();
            const combined = fromValue && toValue ? `${fromValue} - ${toValue}` : (fromValue || toValue);

            if (!combined) {
                return;
            }

            onAdd?.(criteria, combined);

            return;
        }

        if (criteriaMeta?.kind === CriteriaMetaKinds.UnderlyingPair) {
            const underlyingSchemeValue = String(underlyingSchemeElementRef.current?.value ?? "").trim();
            const underlyingIdsValue = String(underlyingIdsElementRef.current?.value ?? "").trim();

            if (!underlyingSchemeValue && !underlyingIdsValue) {
                return;
            }

            onAdd?.(criteria, `${underlyingSchemeValue}||${underlyingIdsValue}`);
            return;
        }

        if (criteriaMeta?.kind === CriteriaMetaKinds.MarketPair) {
            const marketSchemeValue = String(marketSchemeElementRef.current?.value ?? "").trim();
            const marketIdsValue = String(marketIdsElementRef.current?.value ?? "").trim();

            if (!marketSchemeValue && !marketIdsValue) {
                return;
            }

            onAdd?.(criteria, `${marketSchemeValue}||${marketIdsValue}`);

            return;
        }

        if (criteriaMeta?.kind === CriteriaMetaKinds.IssuerPair) {
            const issuerSchemeValue = String(issuerSchemeElementRef.current?.value ?? "").trim();
            const issuerIdsValue = String(issuerIdsElementRef.current?.value ?? "").trim();

            if (!issuerSchemeValue && !issuerIdsValue) {
                return;
            }

            onAdd?.(criteria, `${issuerSchemeValue}||${issuerIdsValue}`);

            return;
        }

        const rawValue = inputElementRef.current?.value;
        let value = rawValue ?? "";

        if (criteriaConfig[criteria]?.type === InputTypes.DateRange) {
            value = coerceDateToYYYYMMDD(rawValue);
        }

        if (!value) {
            return;
        }

        onAdd?.(criteria, value);
    };

    const propertyKey = criteriaMeta?.kind === CriteriaMetaKinds.Single ? criteriaMeta.propertyKey : criteria;
    const property = propertiesByEndpoint?.[propertyKey];

    if (criteriaMeta?.kind === CriteriaMetaKinds.DateRangePair) {
        const fromProperty = propertiesByEndpoint?.[criteriaMeta.fromKey];
        const toProperty = propertiesByEndpoint?.[criteriaMeta.toKey];

        return (
            <div className="input-field">
                <SixDatepicker ref={fromElementRef} type="date" date-format="yyyy-mm-dd" label={fromProperty?.name ?? `${criteria} From`} placeholder="Select date" min={new Date(new Date(1970, 0, 1))}></SixDatepicker>
                <SixDatepicker ref={toElementRef} type="date" date-format="yyyy-mm-dd" label={toProperty?.name ?? `${criteria} To`} placeholder="Select date" min={new Date(new Date(1970, 0, 1))}></SixDatepicker>
                <div className="tooltip">
                    <span className="material-icons-outlined icon">info</span>
                    <span className="tooltiptext">{fromProperty?.description ?? toProperty?.description}</span>
                </div>
                <SixButton type="action" className="criteria-add-btn" onClick={() => handleAdd()}>Add</SixButton>
                <button type="button" className="criterion-delete-btn" aria-label={`Remove ${criteria}`} onClick={() => onRemove(criteria)}>
                    <span className="material-icons-outlined">delete</span>
                </button>
            </div>
        );
    }

    if (criteriaMeta?.kind === CriteriaMetaKinds.NumberPair) {
        const fromProperty = propertiesByEndpoint?.[criteriaMeta.fromKey];
        const toProperty = propertiesByEndpoint?.[criteriaMeta.toKey];

        return (
            <div className="input-field">
                <SixInput ref={fromElementRef} label={fromProperty?.name ?? `${criteria} From`} type="number" placeholder="Enter value"></SixInput>
                <SixInput ref={toElementRef} label={toProperty?.name ?? `${criteria} To`} type="number" placeholder="Enter value"></SixInput>
                <div className="tooltip">
                    <span className="material-icons-outlined icon">info</span>
                    <span className="tooltiptext">{fromProperty?.description ?? toProperty?.description}</span>
                </div>
                <SixButton type="action" className="criteria-add-btn" onClick={() => handleAdd()}>Add</SixButton>
                <button type="button" className="criterion-delete-btn" aria-label={`Remove ${criteria}`} onClick={() => onRemove(criteria)}>
                    <span className="material-icons-outlined">delete</span>
                </button>
            </div>
        );
    }

    if (criteriaMeta?.kind === CriteriaMetaKinds.MarketPair) {
        const marketIdsProperty = propertiesByEndpoint?.[criteriaMeta.marketIdsKey];
        const marketSchemeProperty = propertiesByEndpoint?.[criteriaMeta.marketSchemeKey];

        return (
            <div className="input-field">
                <SixSelect filter ref={marketSchemeElementRef} label={marketSchemeProperty?.name ?? "Market Scheme"} placeholder="Choose value">
                    { (marketSchemeProperty?.enum ?? []).map((enumValue) => (
                        <SixMenuItem key={enumValue} value={enumValue}>
                            {enumValue}
                        </SixMenuItem>
                    )) }
                </SixSelect>
                <SixInput ref={marketIdsElementRef} label={marketIdsProperty?.name ?? "Market IDs"} placeholder="Enter value"></SixInput>
                <div className="tooltip">
                    <span className="material-icons-outlined icon">info</span>
                    <span className="tooltiptext">{marketSchemeProperty?.description ?? marketIdsProperty?.description}</span>
                </div>
                <SixButton type="action" className="criteria-add-btn" onClick={() => handleAdd()}>Add</SixButton>
                <button type="button" className="criterion-delete-btn" aria-label={`Remove ${criteria}`} onClick={() => onRemove(criteria)}>
                    <span className="material-icons-outlined">delete</span>
                </button>
            </div>
        );
    }

    if (criteriaMeta?.kind === CriteriaMetaKinds.UnderlyingPair) {
        const underlyingIdsProperty = propertiesByEndpoint?.[criteriaMeta.underlyingIdsKey];
        const underlyingSchemeProperty = propertiesByEndpoint?.[criteriaMeta.underlyingSchemeKey];

        return (
            <div className="input-field">
                <SixSelect filter ref={underlyingSchemeElementRef} label={underlyingSchemeProperty?.name ?? "Underlying Scheme"} placeholder="Choose value">
                    { (underlyingSchemeProperty?.enum ?? []).map((enumValue) => (
                        <SixMenuItem key={enumValue} value={enumValue}>
                            {enumValue}
                        </SixMenuItem>
                    )) }
                </SixSelect>
                <SixInput ref={underlyingIdsElementRef} label={underlyingIdsProperty?.name ?? "Underlying IDs"} placeholder="Enter value"></SixInput>
                <div className="tooltip">
                    <span className="material-icons-outlined icon">info</span>
                    <span className="tooltiptext">{underlyingSchemeProperty?.description ?? underlyingIdsProperty?.description}</span>
                </div>
                <SixButton type="action" className="criteria-add-btn" onClick={() => handleAdd()}>Add</SixButton>
                <button type="button" className="criterion-delete-btn" aria-label={`Remove ${criteria}`} onClick={() => onRemove(criteria)}>
                    <span className="material-icons-outlined">delete</span>
                </button>
            </div>
        );
    }

    if (criteriaMeta?.kind === CriteriaMetaKinds.IssuerPair) {
        const issuerIdsProperty = propertiesByEndpoint?.[criteriaMeta.issuerIdsKey];
        const issuerSchemeProperty = propertiesByEndpoint?.[criteriaMeta.issuerSchemeKey];

        return (
            <div className="input-field">
                <SixSelect filter ref={issuerSchemeElementRef} label={issuerSchemeProperty?.name ?? "Issuer Scheme"} placeholder="Choose value">
                    { (issuerSchemeProperty?.enum ?? []).map((enumValue) => (
                        <SixMenuItem key={enumValue} value={enumValue}>
                            {enumValue}
                        </SixMenuItem>
                    )) }
                </SixSelect>
                <SixInput ref={issuerIdsElementRef} label={issuerIdsProperty?.name ?? "Issuer IDs"} placeholder="Enter value"></SixInput>
                <div className="tooltip">
                    <span className="material-icons-outlined icon">info</span>
                    <span className="tooltiptext">{issuerSchemeProperty?.description ?? issuerIdsProperty?.description}</span>
                </div>
                <SixButton type="action" className="criteria-add-btn" onClick={() => handleAdd()}>Add</SixButton>
                <button type="button" className="criterion-delete-btn" aria-label={`Remove ${criteria}`} onClick={() => onRemove(criteria)}>
                    <span className="material-icons-outlined">delete</span>
                </button>
            </div>
        );
    }

    if (criteriaConfig[criteria]?.type === InputTypes.DateRange) {
        return (
            <div className="input-field single-input-field">
                <SixDatepicker ref={inputElementRef} type="date" date-format="yyyy-mm-dd" label={property?.name ?? criteria} placeholder="Select date" min={new Date(new Date(1970, 0, 1))}></SixDatepicker>
                <div className="tooltip">
                    <span className="material-icons-outlined icon">info</span>
                    <span className="tooltiptext">{property?.description}</span>
                </div>
                <SixButton type="action" className="criteria-add-btn" onClick={() => handleAdd()}>Add</SixButton>
                <button type="button" className="criterion-delete-btn" aria-label={`Remove ${criteria}`} onClick={() => onRemove(criteria)}>
                    <span className="material-icons-outlined">delete</span>
                </button>
            </div>
        );
    }
        
    if (criteriaConfig[criteria]?.type === InputTypes.NumberRange) {
        return (
            <div className="input-field single-input-field">
                <SixInput ref={inputElementRef} label={property?.name ?? criteria} type="number" placeholder="Enter value" min={0}></SixInput>
                <div className="tooltip">
                    <span className="material-icons-outlined icon">info</span>
                    <span className="tooltiptext">{property?.description}</span>
                </div>
                <SixButton type="action" className="criteria-add-btn" onClick={() => handleAdd()}>Add</SixButton>
                <button type="button" className="criterion-delete-btn" aria-label={`Remove ${criteria}`} onClick={() => onRemove(criteria)}>
                    <span className="material-icons-outlined">delete</span>
                </button>
            </div>
        );
    }

    if (criteriaConfig[criteria]?.type === InputTypes.Select) {
        return (
            <div className="input-field single-input-field">
                <SixSelect filter ref={inputElementRef} label={property?.name ?? criteria} placeholder="Choose value" multiple={property?.type === "array"} clearable={property?.type === "array"}>
                    { (property?.enum ?? []).map((enumValue) => (
                        <SixMenuItem key={enumValue} value={enumValue}>
                            {enumValue}
                        </SixMenuItem>
                    )) }
                </SixSelect>
                <div className="tooltip">
                    <span className="material-icons-outlined icon">info</span>
                    <span className="tooltiptext">{property?.description}</span>
                </div>
                <SixButton type="action" className="criteria-add-btn" onClick={() => handleAdd()}>Add</SixButton>
                <button type="button" className="criterion-delete-btn" aria-label={`Remove ${criteria}`} onClick={() => onRemove(criteria)}>
                    <span className="material-icons-outlined">delete</span>
                </button>
            </div>
        );
    } else {
        return (
            <div className="input-field single-input-field">
                <SixInput ref={inputElementRef} label={property?.name ?? criteria} placeholder="Enter value"></SixInput>
                <div className="tooltip">
                    <span className="material-icons-outlined icon">info</span>
                    <span className="tooltiptext">{property?.description}</span>
                </div>
                <SixButton type="action" className="criteria-add-btn" onClick={() => handleAdd()}>Add</SixButton>
                <button type="button" className="criterion-delete-btn" aria-label={`Remove ${criteria}`} onClick={() => onRemove(criteria)}>
                    <span className="material-icons-outlined">delete</span>
                </button>
            </div>
        );
    }
}

function CriteriaChip(props) {
    const criteria = props.criteria;
    const value = props.value;
    const onRemove = props.onRemove;
    const criteriaMeta = props.criteriaMeta;

    const displayValue = (() => {
        if (criteriaMeta?.kind === CriteriaMetaKinds.MarketPair) {
            const [schemeCandidate = "", idsCandidate = ""] = String(value ?? "").split("||");
            const scheme = schemeCandidate.trim();
            const ids = idsCandidate.trim();

            if (scheme && ids) {
                return `scheme=${scheme}, ids=${ids}`;
            }

            if (scheme) {
                return `scheme=${scheme}`;
            }

            if (ids) {
                return `ids=${ids}`;
            }

            return "";
        }

        if (criteriaMeta?.kind === CriteriaMetaKinds.UnderlyingPair) {
            const [schemeCandidate = "", idsCandidate = ""] = String(value ?? "").split("||");
            const scheme = schemeCandidate.trim();
            const ids = idsCandidate.trim();

            if (scheme && ids) {
                return `scheme=${scheme}, ids=${ids}`;
            }

            if (scheme) {
                return `scheme=${scheme}`;
            }

            if (ids) {
                return `ids=${ids}`;
            }

            return "";
        }

        if (criteriaMeta?.kind === CriteriaMetaKinds.IssuerPair) {
            const [schemeCandidate = "", idsCandidate = ""] = String(value ?? "").split("||");
            const scheme = schemeCandidate.trim();
            const ids = idsCandidate.trim();

            if (scheme && ids) {
                return `scheme=${scheme}, ids=${ids}`;
            }

            if (scheme) {
                return `scheme=${scheme}`;
            }

            if (ids) {
                return `ids=${ids}`;
            }

            return "";
        }

        return String(value ?? "");
    })();

    return (
        <div className="criterion-pill">
            <span>{criteria}: {displayValue}</span>
            <button type="button" className="criterion-pill-remove" data-action="remove-criterion" aria-label={`Remove ${criteria}`} onClick={() => onRemove?.(criteria, value)}>
                <span className="material-icons">close</span>
            </button>
        </div>
    );
}

const APIQueryBuilderForm = () => {
    const {setActiveEndpointTab, endpointInfos, propertiesByEndpoint} = useOutletContext();
    const criteriaSelectorElementRef = useRef("");
    const [criteriaList, setCriteriaList] = useState([]);
    const [availableProperties, setAvailableProperties] = useState(propertiesByEndpoint ?? {});
    const [criteriaFilter, setCriteriaFilter] = useState("");
    const [criteriaChips, setCriteriaChips] = useState([]);
    const [pageSize, setPageSize] = useState("300");
    const [pageId, setPageId] = useState();
    const [copySuccess, setCopySuccess] = useState('');
    const [restRequestIsSending, setRestRequestIsSending] = useState(false);
    const [restRequestOutput, setRestRequestOutput] = useState("");
    const [graphqlRequestIsSending, setGraphqlRequestIsSending] = useState(false);
    const [graphqlRequestOutput, setGraphqlRequestOutput] = useState("");
    const restTextAreaRef = useRef(null);
    const graphqlTextAreaRef = useRef(null);

    const pageName = window.location.pathname.split("/").slice(-1)[0];

    const criteriaMetaByLabel = useMemo(() => buildCriteriaMetaByLabel(propertiesByEndpoint ?? {}), [propertiesByEndpoint]);
    const criteriaOptions = useMemo(() => buildSelectableCriteriaOptions(availableProperties ?? {}), [availableProperties]);

    const restKeyToCamelCase = (key) => {
        const raw = String(key ?? "").trim();
        const normalizeIdAcronyms = (value) => String(value ?? "")
            .replace(/IDs\b/g, "Ids")
            .replace(/ID\b/g, "Id");

        if (!raw) {
            return raw;
        }

        const normalizedRaw = normalizeIdAcronyms(raw);

        if (/^[a-z][a-zA-Z0-9]*$/.test(normalizedRaw)) {
            return normalizedRaw;
        }

        const withoutSeparators = normalizedRaw
            .replace(/[_\-\s]+(.)?/g, (_match, capture) => (capture ? String(capture).toUpperCase() : ""))
            .replace(/[^a-zA-Z0-9]/g, "");

        const normalizedWithoutSeparators = normalizeIdAcronyms(withoutSeparators);

        if (/^[A-Z0-9]+$/.test(normalizedWithoutSeparators)) {
            return normalizedWithoutSeparators.toLowerCase();
        }

        if (/^[A-Z]{2,}[A-Z][a-z]/.test(normalizedWithoutSeparators)) {
            const parts = normalizedWithoutSeparators.match(/^([A-Z]+)([A-Z][a-z].*)$/);
            if (parts) {
                return parts[1].toLowerCase() + parts[2];
            }
        }

        return normalizedWithoutSeparators ? normalizedWithoutSeparators[0].toLowerCase() + normalizedWithoutSeparators.slice(1) : normalizedRaw;
    };

    const restParameterList = useMemo(() => {
        const parts = [];

        for (const chip of criteriaChips) {
            const meta = criteriaMetaByLabel?.[chip.criteria];

            if (!meta) {
                continue;
            }

            if (meta.kind === CriteriaMetaKinds.DateRangePair) {
                const [fromCandidate = "", toCandidate = ""] = String(chip.value ?? "").split(" - ");
                const fromValue = fromCandidate.trim();
                const toValue = toCandidate.trim();

                if (fromValue) {
                    parts.push(`${encodeURIComponent(restKeyToCamelCase(meta.fromKey))}=${encodeURIComponent(fromValue)}`);
                }

                if (toValue) {
                    parts.push(`${encodeURIComponent(restKeyToCamelCase(meta.toKey))}=${encodeURIComponent(toValue)}`);
                }

                continue;
            }

            if (meta.kind === CriteriaMetaKinds.NumberPair) {
                const [fromCandidate = "", toCandidate = ""] = String(chip.value ?? "").split(" - ");
                const fromValue = fromCandidate.trim();
                const toValue = toCandidate.trim();

                if (fromValue) {
                    parts.push(`${encodeURIComponent(restKeyToCamelCase(meta.fromKey))}=${encodeURIComponent(fromValue)}`);
                }

                if (toValue) {
                    parts.push(`${encodeURIComponent(restKeyToCamelCase(meta.toKey))}=${encodeURIComponent(toValue)}`);
                }

                continue;
            }

            if (meta.kind === CriteriaMetaKinds.MarketPair) {
                const [schemeCandidate = "", idsCandidate = ""] = String(chip.value ?? "").split("||");
                const schemeValue = schemeCandidate.trim();
                const idsValue = idsCandidate.trim();

                if (schemeValue) {
                    parts.push(`${encodeURIComponent(restKeyToCamelCase(meta.marketSchemeKey))}=${encodeURIComponent(schemeValue)}`);
                }

                if (idsValue) {
                    parts.push(`${encodeURIComponent(restKeyToCamelCase(meta.marketIdsKey))}=${encodeURIComponent(idsValue)}`);
                }

                continue;
            }

            if (meta.kind === CriteriaMetaKinds.UnderlyingPair) {
                const [schemeCandidate = "", idsCandidate = ""] = String(chip.value ?? "").split("||");
                const schemeValue = schemeCandidate.trim();
                const idsValue = idsCandidate.trim();

                if (schemeValue) {
                    parts.push(`${encodeURIComponent(restKeyToCamelCase(meta.underlyingSchemeKey))}=${encodeURIComponent(schemeValue)}`);
                }

                if (idsValue) {
                    parts.push(`${encodeURIComponent(restKeyToCamelCase(meta.underlyingIdsKey))}=${encodeURIComponent(idsValue)}`);
                }

                continue;
            }

            if (meta.kind === CriteriaMetaKinds.IssuerPair) {
                const [schemeCandidate = "", idsCandidate = ""] = String(chip.value ?? "").split("||");
                const schemeValue = schemeCandidate.trim();
                const idsValue = idsCandidate.trim();

                if (schemeValue) {
                    parts.push(`${encodeURIComponent(restKeyToCamelCase(meta.issuerSchemeKey))}=${encodeURIComponent(schemeValue)}`);
                }

                if (idsValue) {
                    parts.push(`${encodeURIComponent(restKeyToCamelCase(meta.issuerIdsKey))}=${encodeURIComponent(idsValue)}`);
                }

                continue;
            }

            const value = String(chip.value ?? "");

            if (!value) {
                continue;
            }

            const isSelect = criteriaConfig[chip.criteria]?.type === InputTypes.Select;
            const key = restKeyToCamelCase(meta.propertyKey);

            if (isSelect && value.includes(",")) {
                const items = value
                    .split(",")
                    .map((item) => item.trim())
                    .filter((item) => item.length > 0);

                items.forEach((item) => {
                    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
                });

                continue;
            }

            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }

        return parts.length ? `&${parts.join("&")}` : "";
    }, [criteriaChips, criteriaMetaByLabel]);

    const endpointUrl = endpointInfos?.[pageName]?.url ?? "";

    const restRequestPreview = `${backEndEnvironmentUrlToUse()}/web/v2/search/criteriaSearch/${endpointUrl}?size=${encodeURIComponent(pageSize)}${pageId ? `&pageId=${encodeURIComponent(pageId)}` : ""}${restParameterList}`;

    const graphqlValueLiteral = (raw, { treatAsNumber = false, forceString = false } = {}) => {
        const value = String(raw ?? "").trim();

        if (forceString) {
            return JSON.stringify(value);
        }

        if (value.toLowerCase() === "null") {
            return "null";
        }

        if (value === "true" || value === "false") {
            return value;
        }

        if (/^[A-Z][A-Z0-9_]*$/.test(value)) {
            return value;
        }

        if (treatAsNumber && /^-?\d+(\.\d+)?$/.test(value)) {
            return value;
        }

        return JSON.stringify(value);
    };

    const graphqlKeyToCamelCase = (key) => {
        const raw = String(key ?? "").trim();

        if (!raw) {
            return raw;
        }

        if (/^[a-z][a-zA-Z0-9]*$/.test(raw)) {
            return raw;
        }

        const withoutSeparators = raw
            .replace(/[_\-\s]+(.)?/g, (_match, capture) => (capture ? String(capture).toUpperCase() : ""))
            .replace(/[^a-zA-Z0-9]/g, "");

        if (/^[A-Z0-9]+$/.test(withoutSeparators)) {
            return withoutSeparators.toLowerCase();
        }

        if (/^[A-Z]{2,}[A-Z][a-z]/.test(withoutSeparators)) {
            const parts = withoutSeparators.match(/^([A-Z]+)([A-Z][a-z].*)$/);
            if (parts) {
                return parts[1].toLowerCase() + parts[2];
            }
        }

        return withoutSeparators ? withoutSeparators[0].toLowerCase() + withoutSeparators.slice(1) : raw;
    };

    const graphqlCriteriaBlock = useMemo(() => {
        const outerIndent = "                ";
        const innerIndent = "                    ";
        const nestedIndent = `${innerIndent}    `;

        const stripSuffix = (key, suffix) => (key.endsWith(suffix) ? key.slice(0, -suffix.length) : key);
        const baseKeyForDatePair = (fromKey, toKey) => {
            const fromBase = stripSuffix(fromKey, "From");
            const toBase = stripSuffix(toKey, "To");

            if (fromBase && fromBase === toBase) {
                return fromBase;
            }

            return fromBase || toBase;
        };

        const lines = [];

        for (const chip of criteriaChips) {
            const meta = criteriaMetaByLabel?.[chip.criteria];

            if (!meta) {
                continue;
            }

            if (meta.kind === CriteriaMetaKinds.DateRangePair) {
                const [fromCandidate = "", toCandidate = ""] = String(chip.value ?? "").split(" - ");
                const fromValue = fromCandidate.trim();
                const toValue = toCandidate.trim();

                const baseKey = graphqlKeyToCamelCase(baseKeyForDatePair(meta.fromKey, meta.toKey));

                if (!baseKey || (!fromValue && !toValue)) {
                    continue;
                }

                lines.push(`${innerIndent}${baseKey}: {`);

                if (fromValue) {
                    lines.push(`${nestedIndent}from: ${graphqlValueLiteral(fromValue)}`);
                }

                if (toValue) {
                    lines.push(`${nestedIndent}to: ${graphqlValueLiteral(toValue)}`);
                }

                lines.push(`${innerIndent}}`);

                continue;
            }

            if (meta.kind === CriteriaMetaKinds.NumberPair) {
                const [fromCandidate = "", toCandidate = ""] = String(chip.value ?? "").split(" - ");
                const fromValue = fromCandidate.trim();
                const toValue = toCandidate.trim();

                const baseKey = graphqlKeyToCamelCase(baseKeyForDatePair(meta.fromKey, meta.toKey));

                if (!baseKey || (!fromValue && !toValue)) {
                    continue;
                }

                lines.push(`${innerIndent}${baseKey}: {`);

                if (fromValue) {
                    lines.push(`${nestedIndent}from: ${graphqlValueLiteral(fromValue, { treatAsNumber: true })}`);
                }

                if (toValue) {
                    lines.push(`${nestedIndent}to: ${graphqlValueLiteral(toValue, { treatAsNumber: true })}`);
                }

                lines.push(`${innerIndent}}`);

                continue;
            }

            if (meta.kind === CriteriaMetaKinds.MarketPair) {
                const [schemeCandidate = "", idsCandidate = ""] = String(chip.value ?? "").split("||");
                const schemeValue = schemeCandidate.trim();
                const idsValue = idsCandidate.trim();

                if (!schemeValue && !idsValue) {
                    continue;
                }

                lines.push(`${innerIndent}${graphqlKeyToCamelCase("market")}: {`);

                if (schemeValue) {
                    lines.push(`${nestedIndent}scheme: ${graphqlValueLiteral(schemeValue)}`);
                }

                if (idsValue) {
                    lines.push(`${nestedIndent}ids: ${graphqlValueLiteral(idsValue)}`);
                }

                lines.push(`${innerIndent}}`);

                continue;
            }

            if (meta.kind === CriteriaMetaKinds.UnderlyingPair) {
                const [schemeCandidate = "", idsCandidate = ""] = String(chip.value ?? "").split("||");
                const schemeValue = schemeCandidate.trim();
                const idsValue = idsCandidate.trim();

                if (!schemeValue && !idsValue) {
                    continue;
                }

                lines.push(`${innerIndent}${graphqlKeyToCamelCase("underlying")}: {`);

                if (schemeValue) {
                    lines.push(`${nestedIndent}scheme: ${graphqlValueLiteral(schemeValue)}`);
                }

                if (idsValue) {
                    lines.push(`${nestedIndent}ids: ${graphqlValueLiteral(idsValue)}`);
                }

                lines.push(`${innerIndent}}`);

                continue;
            }

            if (meta.kind === CriteriaMetaKinds.IssuerPair) {
                const [schemeCandidate = "", idsCandidate = ""] = String(chip.value ?? "").split("||");
                const schemeValue = schemeCandidate.trim();
                const idsValue = idsCandidate.trim();

                if (!schemeValue && !idsValue) {
                    continue;
                }

                lines.push(`${innerIndent}${graphqlKeyToCamelCase("issuer")}: {`);

                if (schemeValue) {
                    lines.push(`${nestedIndent}scheme: ${graphqlValueLiteral(schemeValue)}`);
                }

                if (idsValue) {
                    lines.push(`${nestedIndent}ids: ${graphqlValueLiteral(idsValue)}`);
                }

                lines.push(`${innerIndent}}`);

                continue;
            }

            const value = String(chip.value ?? "").trim();

            if (!value) {
                continue;
            }

            const treatAsNumber = criteriaConfig[chip.criteria]?.type === InputTypes.NumberRange;
            const isSelect = criteriaConfig[chip.criteria]?.type === InputTypes.Select;
            const isIssuerCountry = chip.criteria === "Issuer Country" || meta.propertyKey === "issuerCountry";
            const isListingCurrency = chip.criteria === "Listing Currency" || meta.propertyKey === "listingCurrency";
            const isNominalCurrency = chip.criteria === "Nominal Currency" || meta.propertyKey === "nominalCurrency";
            const isOptionStrikePriceCurrency = chip.criteria === "Option Strike Price Currency" || meta.propertyKey === "optionStrikePriceCurrency";
            const isRootSymbol = chip.criteria === "Root Symbol" || meta.propertyKey === "rootSymbol";
            const isUnderlyingNominalCurrency = chip.criteria === "Underlying Nominal Currency" || meta.propertyKey === "underlyingNominalCurrency";
            const normalizedValue = (isIssuerCountry || isListingCurrency || isNominalCurrency || isOptionStrikePriceCurrency || isRootSymbol || isUnderlyingNominalCurrency) ? value : value;
            const propertyDefinition = propertiesByEndpoint?.[meta.propertyKey];
            const isFormattedString = propertyDefinition?.type === "string" && Boolean(propertyDefinition?.format);

            if (isSelect && normalizedValue.includes(",")) {
                const items = normalizedValue
                    .split(",")
                    .map((item) => item.trim())
                    .filter((item) => item.length > 0)
                    .map((item) => graphqlValueLiteral(item));

                lines.push(`${innerIndent}${graphqlKeyToCamelCase(meta.propertyKey)}: [${items.join(",")}]`);
                continue;
            }

            if (isFormattedString && normalizedValue.includes(",")) {
                const items = normalizedValue
                    .split(",")
                    .map((item) => item.trim())
                    .filter((item) => item.length > 0)
                    .map((item) => graphqlValueLiteral(item, { forceString: true }));

                lines.push(`${innerIndent}${graphqlKeyToCamelCase(meta.propertyKey)}: [${items.join(",")}]`);
                continue;
            }

            lines.push(`${innerIndent}${graphqlKeyToCamelCase(meta.propertyKey)}: ${graphqlValueLiteral(normalizedValue, { treatAsNumber, forceString: isIssuerCountry || isListingCurrency || isNominalCurrency || isOptionStrikePriceCurrency || isRootSymbol || isUnderlyingNominalCurrency })}`);
        }

        if (!lines.length) {
            return `${outerIndent}criteria: {\n${outerIndent}}`;
        }

        return `${outerIndent}criteria: {\n${lines.join("\n")}\n${outerIndent}}`;
    }, [criteriaChips, criteriaMetaByLabel, propertiesByEndpoint]);

    const graphqlRequestPreview = useMemo(() => {
        return `query ${capitalizeWord(endpointUrl)}CriteriaSearch {
    search {
        criteriaSearch {
            ${endpointUrl} (
${graphqlCriteriaBlock}
                size: ${graphqlValueLiteral(pageSize, { treatAsNumber: true })}${pageId ? `, pageId: ${graphqlValueLiteral(pageId)}` : ""}
            ) {
                paging {
                    nextPageId
                    size
                }
                hits {
                    valor
                    ${endpointUrl === "instruments" ? "isin" : "listingShortName"}
                    ${endpointUrl === "instruments" ? "instrumentShortName" : "listingCurrency"}
                }
            }
        }
    }
}`;
    }, [endpointUrl, graphqlCriteriaBlock, pageId, pageSize]);

    const graphqlEndpointUrl = `${backEndEnvironmentUrlToUse()}/web/${LATEST_MAJOR_VERSION}/graphql?`;

    const sendGraphqlRequest = async () => {
        
        if (!graphqlRequestPreview) {
            return;
        }

        setGraphqlRequestIsSending(true);
        setGraphqlRequestOutput("");

        try {
            const response = await fetch(graphqlEndpointUrl, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "content-type": "application/json",
                    "Accept-Encoding": "gzip",
                },
                body: JSON.stringify({
                    query: graphqlRequestPreview,
                }),
            });

            const contentType = response.headers.get("content-type") ?? "";
            let bodyText = "";

            if (contentType.includes("application/json")) {
                const json = await response.json();
                bodyText = JSON.stringify(json, null, 2);
            } else {
                bodyText = await response.text();
            }

            if (!response.ok) {
                setGraphqlRequestOutput(`HTTP ${response.status} ${response.statusText}\n\n${bodyText}`.trim());
                return;
            }

            setGraphqlRequestOutput(bodyText || `HTTP ${response.status} ${response.statusText}`);
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            setGraphqlRequestOutput(`Request failed: ${message}`);
        } finally {
            setGraphqlRequestIsSending(false);
        }
    };

    const sendRestRequest = async () => {
        if (!restRequestPreview) {
            return;
        }

        setRestRequestIsSending(true);
        setRestRequestOutput("");

        try {
            const response = await fetch(restRequestPreview, {
                method: "GET",
                headers: {
                    Accept: "application/json, text/plain, */*",
                },
            });

            const contentType = response.headers.get("content-type") ?? "";
            let bodyText = "";

            if (contentType.includes("application/json")) {
                const json = await response.json();
                bodyText = JSON.stringify(json, null, 2);
            } else {
                bodyText = await response.text();
            }

            if (!response.ok) {
                setRestRequestOutput(`HTTP ${response.status} ${response.statusText}\n\n${bodyText}`.trim());
                return;
            }

            setRestRequestOutput(bodyText || `HTTP ${response.status} ${response.statusText}`);
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            setRestRequestOutput(`Request failed: ${message}`);
        } finally {
            setRestRequestIsSending(false);
        }
    };

    useEffect(() => {
        setActiveEndpointTab(endpointInfos[pageName].url);
    }, []);

    useEffect(() => {
        const defaultsByPageName = {
            instruments: [],
            listings: [],
        };

        const baseProperties = propertiesByEndpoint ?? {};
        const requestedDefaults = defaultsByPageName[pageName] ?? [];
        const defaults = requestedDefaults.filter((criteria) => baseProperties?.[criteria]);
        const defaultSet = new Set(defaults);

        setCriteriaList(defaults);
        setCriteriaChips([]);
        setAvailableProperties(
            Object.fromEntries(
                Object.entries(baseProperties).filter(([propertyKey, property]) => {
                    const name = property?.name ?? "";
                    return !defaultSet.has(propertyKey) && !defaultSet.has(name);
                })
            )
        );
        setCriteriaFilter("");

        if (criteriaSelectorElementRef.current) {
            criteriaSelectorElementRef.current.value = "";
        }
    }, [propertiesByEndpoint, pageName]);

    const handleCriteriaSelectorChange = () => {
        setCriteriaFilter(criteriaSelectorElementRef.current?.value ?? "");
    };

    function copyToClipboard(ref) {
        ref.current.select();
        document.execCommand('copy');
        setCopySuccess(true);
    };

    const visibleCriteriaOptions = criteriaOptions.filter((option) => {
        if (!criteriaFilter) {
            return true;
        }

        return option.label.toLowerCase().includes(criteriaFilter.toLowerCase());
    });

    const addCriteriaOption = (option) => {
        if (!option?.label) {
            return;
        }

        setCriteriaList((previous) => {
            if (previous.includes(option.label)) {
                return previous;
            }

            return previous.concat(option.label);
        });

        setAvailableProperties((previous) => {
            const entries = Object.entries(previous);

            if (option.kind === CriteriaMetaKinds.DateRangePair) {
                return Object.fromEntries(entries.filter(([key]) => key !== option.fromKey && key !== option.toKey));
            }

            if (option.kind === CriteriaMetaKinds.NumberPair) {
                return Object.fromEntries(entries.filter(([key]) => key !== option.fromKey && key !== option.toKey));
            }

            if (option.kind === CriteriaMetaKinds.MarketPair) {
                return Object.fromEntries(entries.filter(([key]) => key !== option.marketSchemeKey && key !== option.marketIdsKey));
            }

            if (option.kind === CriteriaMetaKinds.UnderlyingPair) {
                return Object.fromEntries(entries.filter(([key]) => key !== option.underlyingSchemeKey && key !== option.underlyingIdsKey));
            }

            if (option.kind === CriteriaMetaKinds.IssuerPair) {
                return Object.fromEntries(entries.filter(([key]) => key !== option.issuerSchemeKey && key !== option.issuerIdsKey));
            }

            return Object.fromEntries(entries.filter(([key]) => key !== option.propertyKey));
        });

        setCriteriaFilter("");

        if (criteriaSelectorElementRef.current) {
            criteriaSelectorElementRef.current.value = "";
        }
    };

    const removeCriteria = (criteriaToRemove) => {
        setCriteriaList((previous) => previous.filter((criteria) => criteria !== criteriaToRemove));
        setCriteriaChips((previous) => previous.filter((chip) => chip.criteria !== criteriaToRemove));

        const meta = criteriaMetaByLabel?.[criteriaToRemove];

        if (!meta) {
            return;
        }

        setAvailableProperties((previous) => {
            if (meta.kind === CriteriaMetaKinds.DateRangePair) {
                const fromProperty = propertiesByEndpoint?.[meta.fromKey];
                const toProperty = propertiesByEndpoint?.[meta.toKey];
                return {
                    ...previous,
                    ...(fromProperty ? { [meta.fromKey]: fromProperty } : {}),
                    ...(toProperty ? { [meta.toKey]: toProperty } : {}),
                };
            }

            if (meta.kind === CriteriaMetaKinds.NumberPair) {
                const fromProperty = propertiesByEndpoint?.[meta.fromKey];
                const toProperty = propertiesByEndpoint?.[meta.toKey];
                return {
                    ...previous,
                    ...(fromProperty ? { [meta.fromKey]: fromProperty } : {}),
                    ...(toProperty ? { [meta.toKey]: toProperty } : {}),
                };
            }

            if (meta.kind === CriteriaMetaKinds.MarketPair) {
                const marketSchemeProperty = propertiesByEndpoint?.[meta.marketSchemeKey];
                const marketIdsProperty = propertiesByEndpoint?.[meta.marketIdsKey];
                return {
                    ...previous,
                    ...(marketSchemeProperty ? { [meta.marketSchemeKey]: marketSchemeProperty } : {}),
                    ...(marketIdsProperty ? { [meta.marketIdsKey]: marketIdsProperty } : {}),
                };
            }

            if (meta.kind === CriteriaMetaKinds.UnderlyingPair) {
                const underlyingSchemeProperty = propertiesByEndpoint?.[meta.underlyingSchemeKey];
                const underlyingIdsProperty = propertiesByEndpoint?.[meta.underlyingIdsKey];
                return {
                    ...previous,
                    ...(underlyingSchemeProperty ? { [meta.underlyingSchemeKey]: underlyingSchemeProperty } : {}),
                    ...(underlyingIdsProperty ? { [meta.underlyingIdsKey]: underlyingIdsProperty } : {}),
                };
            }

            if (meta.kind === CriteriaMetaKinds.IssuerPair) {
                const issuerSchemeProperty = propertiesByEndpoint?.[meta.issuerSchemeKey];
                const issuerIdsProperty = propertiesByEndpoint?.[meta.issuerIdsKey];
                return {
                    ...previous,
                    ...(issuerSchemeProperty ? { [meta.issuerSchemeKey]: issuerSchemeProperty } : {}),
                    ...(issuerIdsProperty ? { [meta.issuerIdsKey]: issuerIdsProperty } : {}),
                };
            }

            const property = propertiesByEndpoint?.[meta.propertyKey];

            if (!property) {
                return previous;
            }

            return {
                ...previous,
                [meta.propertyKey]: property,
            };
        });
    };

    const addCriteriaChip = (criteria, value) => {
        setCriteriaChips((previous) => {
            const exists = previous.some((chip) => chip.criteria === criteria && chip.value === value);

            if (exists) {
                return previous;
            }

            return previous.concat({
                id: `${criteria}:${value}`,
                criteria,
                value,
            });
        });

        setCriteriaList((previous) => previous.filter((item) => item !== criteria));
    };

    const removeCriteriaChip = (criteria, value) => {
        setCriteriaChips((previous) => previous.filter((chip) => !(chip.criteria === criteria && chip.value === value)));

        const meta = criteriaMetaByLabel?.[criteria];

        if (!meta) {
            return;
        }

        setAvailableProperties((previous) => {
            if (meta.kind === CriteriaMetaKinds.DateRangePair) {
                const fromProperty = propertiesByEndpoint?.[meta.fromKey];
                const toProperty = propertiesByEndpoint?.[meta.toKey];
                return {
                    ...previous,
                    ...(fromProperty ? { [meta.fromKey]: fromProperty } : {}),
                    ...(toProperty ? { [meta.toKey]: toProperty } : {}),
                };
            }

            if (meta.kind === CriteriaMetaKinds.NumberPair) {
                const fromProperty = propertiesByEndpoint?.[meta.fromKey];
                const toProperty = propertiesByEndpoint?.[meta.toKey];
                return {
                    ...previous,
                    ...(fromProperty ? { [meta.fromKey]: fromProperty } : {}),
                    ...(toProperty ? { [meta.toKey]: toProperty } : {}),
                };
            }

            if (meta.kind === CriteriaMetaKinds.MarketPair) {
                const marketSchemeProperty = propertiesByEndpoint?.[meta.marketSchemeKey];
                const marketIdsProperty = propertiesByEndpoint?.[meta.marketIdsKey];
                return {
                    ...previous,
                    ...(marketSchemeProperty ? { [meta.marketSchemeKey]: marketSchemeProperty } : {}),
                    ...(marketIdsProperty ? { [meta.marketIdsKey]: marketIdsProperty } : {}),
                };
            }

            if (meta.kind === CriteriaMetaKinds.UnderlyingPair) {
                const underlyingSchemeProperty = propertiesByEndpoint?.[meta.underlyingSchemeKey];
                const underlyingIdsProperty = propertiesByEndpoint?.[meta.underlyingIdsKey];
                return {
                    ...previous,
                    ...(underlyingSchemeProperty ? { [meta.underlyingSchemeKey]: underlyingSchemeProperty } : {}),
                    ...(underlyingIdsProperty ? { [meta.underlyingIdsKey]: underlyingIdsProperty } : {}),
                };
            }

            if (meta.kind === CriteriaMetaKinds.IssuerPair) {
                const issuerSchemeProperty = propertiesByEndpoint?.[meta.issuerSchemeKey];
                const issuerIdsProperty = propertiesByEndpoint?.[meta.issuerIdsKey];
                return {
                    ...previous,
                    ...(issuerSchemeProperty ? { [meta.issuerSchemeKey]: issuerSchemeProperty } : {}),
                    ...(issuerIdsProperty ? { [meta.issuerIdsKey]: issuerIdsProperty } : {}),
                };
            }

            const property = propertiesByEndpoint?.[meta.propertyKey];

            if (!property) {
                return previous;
            }

            return {
                ...previous,
                [meta.propertyKey]: property,
            };
        });
    };
    
    return (
        <>
            <div className="tab-explore-form">
                <h2 className="section-title">{endpointInfos[pageName].displayName}</h2>
                <div className="tab-explore-criteria-selector">
                    <SixSelect
                        placeholder="Add Search Criteria"
                        ref={criteriaSelectorElementRef}
                        filter
                        clearable
                        id="selector"
                        onChange={() => handleCriteriaSelectorChange()}>
                        { visibleCriteriaOptions.map((option) => (
                            <SixMenuItem key={option.id} value={option.label} onClick={() => addCriteriaOption(option)}>
                                {option.label}
                            </SixMenuItem>
                        )) }
                    </SixSelect>
                </div>
                <div className={"tab-explore-criteria-list " + (criteriaList.length > 0 ? '' : 'hidden')}>
                    {criteriaList.map((criteria, index) => (
                        <InputField
                            key={index}
                            criteria={criteria}
                            criteriaMeta={criteriaMetaByLabel?.[criteria]}
                            propertiesByEndpoint={propertiesByEndpoint}
                            onRemove={removeCriteria}
                            onAdd={addCriteriaChip}
                        />
                    ))}
                </div>
                <div className="tab-explore-criteria-chips">
                    {criteriaChips.map((chip) => (
                        <CriteriaChip
                            key={chip.id}
                            criteria={chip.criteria}
                            value={chip.value}
                            criteriaMeta={criteriaMetaByLabel?.[chip.criteria]}
                            onRemove={removeCriteriaChip}
                        />
                    ))}
                </div>
                <div className="tab-explore-criteria-list tab-explore-criteria-default-list">
                    <div className="input-field">
                        <SixInput
                            label={"Page Size"}
                            type="number"
                            placeholder="Enter value"
                            value={pageSize}
                            onChange={(event) => setPageSize(event?.target?.value ?? "")}
                            onInput={(event) => setPageSize(event?.target?.value ?? "")}
                        ></SixInput>
                    </div>
                    <div className="input-field">
                        <SixInput
                            label={"Page ID"}
                            placeholder="Enter value"
                            value={pageId}
                            onChange={(event) => setPageId(event?.target?.value ?? "")}
                            onInput={(event) => setPageId(event?.target?.value ?? "")}
                        ></SixInput>
                    </div>
                </div>
                <div className="response-section">
                    <div className="response-title">Request Review</div>
                    <SixTabGroup>
                        <SixTab slot="nav" panel="rest">REST</SixTab>
                        <SixTab slot="nav" panel="graphql">GraphQL</SixTab>

                        <SixTabPanel name="rest">
                            <div className="request-preview">
                                <textarea ref={restTextAreaRef} className="request-preview-field" value={restRequestPreview} readOnly />
                                <button onClick={() => copyToClipboard(restTextAreaRef)} type="button" className={`request-copy-action` + (copySuccess ? ' copied' : '')}>
                                    <span className="material-icons-outlined">content_copy</span>
                                </button>
                            </div>
                            <div className="request-buttons">
                                <SixButton type="action" className="request-send-action" onClick={() => sendRestRequest()} disabled={restRequestIsSending}>
                                    {restRequestIsSending ? "Sending..." : "Send REST Request"}
                                </SixButton>
                            </div>
                            <div className="request-output">
                                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{restRequestOutput}</pre>
                            </div>
                        </SixTabPanel>
                        <SixTabPanel name="graphql">
                            <div className="request-preview">
                                <textarea rows={25} ref={graphqlTextAreaRef} className="request-preview-field" value={graphqlRequestPreview} readOnly />
                                <button onClick={() => copyToClipboard(graphqlTextAreaRef)} type="button" className={`request-copy-action` + (copySuccess ? ' copied' : '')}>
                                    <span className="material-icons-outlined">content_copy</span>
                                </button>
                            </div>
                            <div className="request-buttons">
                                <SixButton type="action" className="request-send-action" onClick={() => sendGraphqlRequest()} disabled={graphqlRequestIsSending}>
                                    {graphqlRequestIsSending ? "Sending..." : "Send GraphQL Request"}
                                </SixButton>
                            </div>
                            <div className="request-output">
                                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{graphqlRequestOutput}</pre>
                            </div>
                        </SixTabPanel>
                    </SixTabGroup>
                </div>
            </div>
        </>
    );
};

export default APIQueryBuilderForm;